/* © 2018-2022 TakuLabs Ltd. All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential */

import { Component, Input, ViewChild, ElementRef } from "@angular/core";
import { InvoiceHelpers } from "src/app/utility/InvoiceHelpers";
import { DBService } from "src/app/shared/services/db.service";
import { MessageService } from "primeng/api";
import { Inventory, InventoryStock } from "src/app/core/inventory/inventory/inventory";
import { FormArray, UntypedFormArray, UntypedFormBuilder } from "@angular/forms";
import { InventoryStockComponent } from "src/app/core/inventory/inventory-stock/inventory-stock.component";
import { NestedFormDisplayMode } from "src/app/forms/nested-form/nested-form.component";
import { WebHelpers } from "src/app/utility/WebHelpers";
import { InventoryDoc, InventoryDocLine } from "../inventory-document/inventory-document";
import { DiscountType } from "../../sale-document/sale-doc/sale-doc";
import { MonetaryHelpers } from "src/app/utility/MonetaryHelpers";
import _ from "lodash";

@Component({
  selector: "taku-inventory-doc-transactions",
  templateUrl: "./inventory-doc-transactions.component.html",
  styleUrls: ["./inventory-doc-transactions.component.scss"],
})
export class InventoryDocTransactionsComponent {
  @Input() inventoryDoc: InventoryDoc;
  @ViewChild("saleLines", { static: true }) saleLinesWrapper: ElementRef;

  DiscountType = DiscountType;
  inventoryStocksByInventoryId: Record<number, InventoryStock[]> = {};
  _activeDialog = false;
  _dialogContent = "";
  _displayMode: NestedFormDisplayMode = NestedFormDisplayMode.TABULAR_DATA;

  _inventoryStockFormArray: UntypedFormArray = InventoryStockComponent.initArray(this.fb);
  InvoiceHelpers = InvoiceHelpers;

  constructor(private dbService: DBService, private messageService: MessageService, public fb: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.retrieveInventoryStocks();
  }

  private retrieveInventoryStocks(): void {
    const _inventoryIds = [];
    const _inventoryStocksByInventoryId: Record<number, InventoryStock[]> = {};

    this.inventoryDoc.inventoryDocLines?.forEach((saleLine) => {
      if (saleLine.docLine?.inventory?.id) {
        _inventoryIds.push(saleLine.docLine?.inventory?.id);
      }
    });

    const _filter = {
      inventoryId: {
        value: _inventoryIds,
        matchMode: "in",
      },
    };

    this.dbService.getRows("inventoryStock", JSON.stringify(_filter), 0, 50).subscribe((results) => {
      if (results?.rows?.length > 0) {
        const _inventoryStocks = results.rows;

        _inventoryStocks.forEach((_inventoryStock) => {
          if (!_inventoryStocksByInventoryId.hasOwnProperty(_inventoryStock.inventoryId)) {
            _inventoryStocksByInventoryId[_inventoryStock.inventoryId] = [_inventoryStock];
          } else {
            _inventoryStocksByInventoryId[_inventoryStock.inventoryId].push(_inventoryStock);
          }
        });
      }

      if (Object.keys(_inventoryStocksByInventoryId).length !== 0) {
        this.inventoryStocksByInventoryId = _inventoryStocksByInventoryId;
      }
    });
  }

  populateStockAvailabilityDialog(inventoryId: number): void {
    this._inventoryStockFormArray = InventoryStockComponent.setArray(
      this.fb,
      this.inventoryStocksByInventoryId[inventoryId]
    );
  }

  get tableScrollHeight(): string {
    // calc(90vh - 45pt)
    return WebHelpers.isMobileScreen() ? "330pt" : "316pt";
  }

  get _isMobile(): boolean {
    return WebHelpers.isMobileScreen();
  }

  get hasScrollbars(): boolean {
    const linesEl = this.saleLinesWrapper.nativeElement;
    return linesEl.scrollHeight > linesEl.clientHeight;
  }

  get inventoryDocTotal(): number {
    const total =
      this.inventoryDocSubTotal -
      Number(this.inventoryDocGeneralDiscount || 0) +
      Number(this.inventoryDoc.shipperChargeAmount || 0) * Number(this.inventoryDoc.doc.currencyConversionRate || 1) +
      _.sumBy(this.inventoryDoc.groupedTaxLines, (tax) => tax.total);
    return total;
  }

  get inventoryDocSubTotal(): number {
    let subTotal = 0;
    const docLines: InventoryDocLine[] = this.inventoryDoc.inventoryDocLines;
    if (docLines) docLines.forEach((inventoryDocLine) => (subTotal += inventoryDocLine.subTotal));
    return subTotal;
  }

  get inventoryDocGeneralDiscount(): number {
    const discountType = this.inventoryDoc.discountType;
    const discountAmount = this.inventoryDoc.discountAmount;
    let totalDiscount = 0;

    if (typeof discountAmount === "undefined" || discountAmount === null) return 0;

    switch (discountType) {
      case DiscountType.Fixed:
        totalDiscount = discountAmount * this.inventoryDoc.doc.currencyConversionRate;
        break;
      case DiscountType.Percentage:
        totalDiscount = 0;
        if (this.inventoryDoc.inventoryDocLines) {
          this.inventoryDoc.inventoryDocLines.forEach(
            (inventoryDocLine) =>
              (totalDiscount += MonetaryHelpers.roundToDecimalPlaces(
                inventoryDocLine.totalDiscountAmount * this.inventoryDoc.doc.currencyConversionRate
              ))
          );
        }

        break;
    }
    return MonetaryHelpers.roundToDecimalPlaces(totalDiscount);
  }

  inventoryVariantsStr(_inventory: Inventory): string {
    return _inventory.inventoryVariants
      ?.map((_inventoryVariant) => _inventoryVariant.inventoryOptionsSettingValue?.optionValue)
      .join(", ");
  }

  getTaxFontColor(): string {
    if (this.inventoryDoc.groupedTaxLines.findIndex((row) => row.saleTaxRule === null) !== -1) {
      return "red";
    } else {
      return "black";
    }
  }

  isTaxConflict(): boolean {
    return this.inventoryDoc.groupedTaxLines.findIndex((row) => row.saleTaxRule === null) !== -1;
  }

  unitCost(inventoryDocLine: InventoryDocLine): number {
    if (inventoryDocLine.allocatedDiscountAmount !== 0) {
      if (inventoryDocLine.allocatedDiscountType === DiscountType.Fixed) {
        return MonetaryHelpers.roundToDecimalPlaces(
          inventoryDocLine.unitCost -
            (inventoryDocLine.qty !== 0 ? inventoryDocLine.allocatedDiscountAmount / inventoryDocLine.qty : 0),
          4
        );
      } else {
        return MonetaryHelpers.roundToDecimalPlaces(
          inventoryDocLine.unitCost - (inventoryDocLine.allocatedDiscountAmount * inventoryDocLine.unitCost) / 100,
          4
        );
      }
    } else {
      return inventoryDocLine.unitCost;
    }
  }

  convertedUnitCost(inventoryDocLine: InventoryDocLine): number {
    if (this.inventoryDoc?.doc?.currencyConversionRate) {
      return MonetaryHelpers.roundToDecimalPlaces(
        Number(this.unitCost(inventoryDocLine)) * this.inventoryDoc.doc.currencyConversionRate,
        4
      );
    }
    return MonetaryHelpers.roundToDecimalPlaces(Number(this.unitCost(inventoryDocLine)), 4);
  }

  totalDiscountAmount(inventoryDocLine: InventoryDocLine): number {
    switch (inventoryDocLine.allocatedDiscountType) {
      case DiscountType.Fixed:
        return MonetaryHelpers.roundToDecimalPlaces(+inventoryDocLine.allocatedDiscountAmount);

      case DiscountType.Percentage:
        return MonetaryHelpers.roundToDecimalPlaces(
          (+inventoryDocLine.qty * +inventoryDocLine.unitCost * +inventoryDocLine.allocatedDiscountAmount) / 100
        );

      default:
        return 0;
    }
  }

  convertedTotalDiscountAmount(inventoryDocLine: InventoryDocLine): number {
    return MonetaryHelpers.roundToDecimalPlaces(
      inventoryDocLine.totalDiscountAmount * this.inventoryDoc.doc.currencyConversionRate
    );
  }

  convertedUnitDiscount(inventoryDocLine: InventoryDocLine): number {
    return +inventoryDocLine.qty === 0
      ? 0
      : MonetaryHelpers.roundToDecimalPlaces(
          this.convertedTotalDiscountAmount(inventoryDocLine) / +inventoryDocLine.qty,
          2
        );
  }
}

/* © 2018-2022 TakuLabs Ltd. All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential */
